import React from 'react';
import { Modal, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import FormInput from '../../global/Forms/FormInput';

type EditSheetModalProps = {
  showModal: boolean;
  sheetName: string;
  existingSheetNames?: string[];
  currentSheetName?: string;
  onSheetNameChange: (name: string) => void;
  onCancel: () => void;
  onConfirm: (sheetName: string) => Promise<void>;
  isEditMode?: boolean;
};

const createValidationSchema = (
  t: (key: string) => string,
  existingSheetNames?: string[],
  currentSheetName?: string
) =>
  Yup.object().shape({
    sheetName: Yup.string()
      .required(t('validation:required'))
      .max(31, t('exportBuilderAdvanced:sheetNameTooLong'))
      .test(
        'invalid-chars',
        t('exportBuilderAdvanced:sheetNameInvalidChars'),
        (value: string | undefined) => !value || !/[/\\?*:[\]]/.test(value)
      )
      .test(
        'reserved-word',
        t('exportBuilderAdvanced:sheetNameReserved'),
        (value: string | undefined) => !value || value.toLowerCase() !== 'history'
      )
      .test(
        'duplicate-name',
        t('exportBuilderAdvanced:sheetNameExists'),
        (value: string | undefined) => {
          if (!value || !existingSheetNames) return true;
          // In edit mode, allow the current sheet name (no change case)
          if (currentSheetName && value.toLowerCase() === currentSheetName.toLowerCase())
            return true;
          return !existingSheetNames.some(name => name.toLowerCase() === value.toLowerCase());
        }
      ),
  });

const EditSheetModal: React.FC<EditSheetModalProps> = ({
  showModal,
  sheetName,
  existingSheetNames,
  currentSheetName,
  onSheetNameChange,
  onCancel,
  onConfirm,
  isEditMode = false,
}) => {
  const { t } = useTranslation();

  const validationSchema = createValidationSchema(t, existingSheetNames, currentSheetName);

  const formMethods = useForm({
    defaultValues: {
      sheetName,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const { handleSubmit, formState, reset, watch } = formMethods;

  React.useEffect(() => {
    if (showModal) {
      reset({ sheetName });
    }
  }, [showModal, sheetName, reset]);

  const currentFormValue = watch('sheetName');
  const hasNameChanged = isEditMode ? currentFormValue?.trim() !== currentSheetName : true;

  const onSubmit = async (values: { sheetName: string }) => {
    onSheetNameChange(values.sheetName);
    await onConfirm(values.sheetName);
  };

  const modalTitle = isEditMode
    ? t('exportBuilderAdvanced:renameSheet')
    : t('exportBuilderAdvanced:addNewSheet');

  const okText = isEditMode
    ? t('exportBuilderAdvanced:renameSheetButton')
    : t('exportBuilderAdvanced:addSheetButton');

  return (
    <Modal
      title={modalTitle}
      open={showModal}
      onOk={handleSubmit(onSubmit)}
      onCancel={onCancel}
      okText={okText}
      cancelText={t('common:cancel')}
      okButtonProps={{
        disabled: !formState.isValid || formState.isSubmitting || !hasNameChanged,
        loading: formState.isSubmitting,
      }}
    >
      <FormProvider {...formMethods}>
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <FormInput
            name="sheetName"
            label={t('exportBuilderAdvanced:sheetNameLabel')}
            placeholder={isEditMode ? currentSheetName : t('exportBuilderAdvanced:enterSheetName')}
            autoFocus
            required
            useHookForm
          />
        </Form>
      </FormProvider>
    </Modal>
  );
};

export default EditSheetModal;
